import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { UserCircle, Plus, Pencil, X, Check } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import CustomFieldsForm from "./CustomFieldsForm";
import { toast } from "./ui/sonner";
import PhoneInput, { applyMask, stripMask } from "./PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIPOS_CONTATO = ["Telefone", "Celular", "Email"];
const isPhoneType = (t) => t === "Telefone" || t === "Celular";
const maskPhone = (v) => { v = v.replace(/\D/g, "").slice(0, 11); if (v.length > 6) v = v.length === 11 ? `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}` : v.length === 10 ? `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}` : `(${v.slice(0,2)}) ${v.slice(2)}`; else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`; else if (v.length > 0) v = `(${v}`; return v; };

export default function ContatoFormDialog({ open, onClose, editItem = null, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ nome: "", tipos_contato: [], custom_fields: {} });
  const [customFields, setCustomFields] = useState([]);
  const [formLayout, setFormLayout] = useState([]);
  const [formSections, setFormSections] = useState([]);
  const [newTipo, setNewTipo] = useState({ tipo: "Celular", ddi: null, valor: "" });
  const [editTipoIdx, setEditTipoIdx] = useState(null);
  const [editTipoVal, setEditTipoVal] = useState({ ddi: null, valor: "" });
  const [addTipoOpen, setAddTipoOpen] = useState(false);
  const [deleteTipoIdx, setDeleteTipoIdx] = useState(null);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#F43F5E";
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const defaultDdi = appearance.default_ddi || "55";
  const defaultCountry = appearance.ddi_country || "BR";

  const fetchData = useCallback(async () => {
    try {
      const [cfRes, layoutRes] = await Promise.all([
        axios.get(`${API}/custom-fields?module=contatos`, { withCredentials: true }),
        axios.get(`${API}/form-layout/contatos`, { withCredentials: true }),
      ]);
      setCustomFields(cfRes.data);
      setFormLayout(layoutRes.data.fields || []);
      setFormSections(layoutRes.data.sections || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
      setForm(editItem
        ? { nome: editItem.nome || "", tipos_contato: editItem.tipos_contato || [], custom_fields: editItem.custom_fields || {} }
        : { nome: "", tipos_contato: [], custom_fields: {} });
      setEditTipoIdx(null);
      setAddTipoOpen(false);
    }
  }, [open, editItem, fetchData]);

  const maxDigits = (phoneMask.match(/#/g) || []).length;

  const validatePhone = (valor, tipo) => {
    if (!isPhoneType(tipo)) return true;
    const digits = stripMask(valor);
    if (digits.length < maxDigits) {
      toast.error(`Telefone incompleto. Esperado ${maxDigits} dígitos, informado ${digits.length}.`);
      return false;
    }
    return true;
  };

  const autoSave = async (updatedTipos) => {
    if (!editItem) return; // only auto-save when editing an existing contact
    try {
      await axios.put(`${API}/contatos/${editItem.id}`, { ...form, tipos_contato: updatedTipos }, { withCredentials: true });
      toast.success("Contato atualizado.");
      if (onSaved) onSaved();
    } catch { toast.error("Erro ao salvar contato."); }
  };

  const addTipoContato = () => {
    if (!newTipo.valor.trim()) return;
    if (!validatePhone(newTipo.valor, newTipo.tipo)) return;
    const tc = { tipo: newTipo.tipo, ddi: newTipo.ddi || defaultDdi, valor: isPhoneType(newTipo.tipo) ? stripMask(newTipo.valor) : newTipo.valor.trim() };
    const updated = [...form.tipos_contato, tc];
    setForm(prev => ({ ...prev, tipos_contato: updated }));
    setNewTipo({ tipo: "Celular", ddi: defaultDdi, valor: "" }); setAddTipoOpen(false);
    autoSave(updated);
  };
  const removeTipo = (idx) => {
    const updated = form.tipos_contato.filter((_, i) => i !== idx);
    setForm(prev => ({ ...prev, tipos_contato: updated }));
    autoSave(updated);
  };
  const startEditTipo = (idx) => {
    const tc = form.tipos_contato[idx];
    setEditTipoIdx(idx);
    setEditTipoVal({ ddi: tc.ddi || defaultDdi, valor: tc.valor || "" });
  };
  const saveEditTipo = () => {
    const tc = form.tipos_contato[editTipoIdx];
    if (!validatePhone(editTipoVal.valor, tc.tipo)) return;
    const updated = [...form.tipos_contato];
    updated[editTipoIdx] = { ...updated[editTipoIdx], ddi: editTipoVal.ddi || defaultDdi, valor: isPhoneType(updated[editTipoIdx].tipo) ? stripMask(editTipoVal.valor) : editTipoVal.valor.trim() };
    setForm(prev => ({ ...prev, tipos_contato: updated }));
    setEditTipoIdx(null);
    autoSave(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Auto-commit any pending tipo contato that the user typed but didn't confirm
    let finalForm = { ...form };
    if (addTipoOpen && newTipo.valor.trim()) {
      finalForm = { ...finalForm, tipos_contato: [...finalForm.tipos_contato, { ...newTipo }] };
    }
    if (editTipoIdx !== null && editTipoVal.valor.trim()) {
      const updated = [...finalForm.tipos_contato];
      updated[editTipoIdx] = { ...updated[editTipoIdx], valor: isPhoneType(updated[editTipoIdx].tipo) ? stripMask(editTipoVal.valor) : editTipoVal.valor.trim() };
      finalForm = { ...finalForm, tipos_contato: updated };
    }
    // Validate name
    if (!finalForm.nome.trim()) {
      toast.error(t("contatos.required_name") || "O nome do contato é obrigatório.");
      return;
    }
    if (finalForm.tipos_contato.length === 0) {
      toast.error(t("contatos.required_contact_type"));
      return;
    }
    try {
      let res;
      if (editItem) res = await axios.put(`${API}/contatos/${editItem.id}`, finalForm, { withCredentials: true });
      else res = await axios.post(`${API}/contatos`, finalForm, { withCredentials: true });
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) { console.error(err); }
  };

  const orderedLayout = formLayout.length > 0
    ? [...formLayout].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [
        { field_name: "nome", fixed: true, field_width: "100" },
        { field_name: "tipos_contato", fixed: true, field_width: "100" },
        ...customFields.map(cf => ({ field_name: cf.field_name, fixed: false, field_width: "100" })),
      ];
  const sectionFieldNames = new Set();
  formSections.forEach(sec => (sec.fields || []).forEach(f => sectionFieldNames.add(f.field_name)));
  const filteredLayout = orderedLayout.filter(f => !sectionFieldNames.has(f.field_name));

  const fixedRenderers = {
    nome: () => <div key="nome"><Label className="text-slate-700">{t("common.name")}</Label><Input value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} /></div>,
    tipos_contato: () => (
      <div key="tipos_contato">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700">{t("contatos.contact_types")}</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => setAddTipoOpen(true)} className="h-7 px-2 text-xs gap-1"><Plus className="w-3 h-3" />{t("common.add")}</Button>
        </div>

        {/* Add new tipo */}
        {addTipoOpen && (
          <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center">
              <select value={newTipo.tipo} onChange={e => setNewTipo({...newTipo, tipo: e.target.value, valor: ""})}
                className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white outline-none flex-shrink-0">
                {TIPOS_CONTATO.map(tipo => <option key={tipo} value={tipo}>{t(`contatos.tipo_${tipo.toLowerCase()}`) || tipo}</option>)}
              </select>
              <div className="flex-1">
                {isPhoneType(newTipo.tipo) ? (
                  <PhoneInput
                    value={{ ddi: newTipo.ddi || defaultDdi, valor: newTipo.valor }}
                    onChange={v => setNewTipo({...newTipo, ddi: v.ddi, valor: v.valor})}
                    mask={phoneMask} defaultDdi={defaultDdi} defaultCountry={defaultCountry}
                  />
                ) : (
                  <Input className="h-9 text-sm" value={newTipo.valor}
                    onChange={e => setNewTipo({...newTipo, valor: e.target.value})}
                    placeholder="email@exemplo.com"
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTipoContato())} />
                )}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addTipoContato}
                className="h-7 w-7 p-0 flex-shrink-0">
                <Check className="w-3.5 h-3.5" />
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setAddTipoOpen(false)}
                className="h-7 w-7 p-0 flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Existing tipos */}
        {form.tipos_contato.length > 0 && (
          <div className="space-y-1.5">
            {form.tipos_contato.map((tc, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                {/* Type label — vertically centered */}
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide w-14 shrink-0">
                  {t(`contatos.tipo_${tc.tipo?.toLowerCase()}`) || tc.tipo}
                </span>

                {editTipoIdx === i ? (
                  <div className="flex items-center gap-2 flex-1">
                    {isPhoneType(tc.tipo) ? (
                      <div className="flex-1">
                        <PhoneInput value={editTipoVal} onChange={v => setEditTipoVal(v)}
                          mask={phoneMask} defaultDdi={defaultDdi} defaultCountry={defaultCountry} />
                      </div>
                    ) : (
                      <Input value={editTipoVal.valor} onChange={e => setEditTipoVal({...editTipoVal, valor: e.target.value})}
                        className="flex-1 h-8 text-sm" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), saveEditTipo())} />
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={saveEditTipo}
                      className="h-7 w-7 p-0 flex-shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditTipoIdx(null)}
                      className="h-7 w-7 p-0 flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      {isPhoneType(tc.tipo) ? (
                        <span className="text-sm text-slate-700">
                          +{tc.ddi || defaultDdi} {applyMask(tc.valor || "", phoneMask)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-700">{tc.valor}</span>
                      )}
                    </div>
                    <button type="button" onClick={() => startEditTipo(i)} className="text-slate-400 hover:text-sky-500 flex-shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => setDeleteTipoIdx(i)} className="text-slate-400 hover:text-rose-500 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
  };

  const renderItem = (item) => {
    if (item.fixed && fixedRenderers[item.field_name]) return fixedRenderers[item.field_name]();
    if (!item.fixed) { const cf = customFields.find(c => c.field_name === item.field_name); if (cf) return <CustomFieldsForm key={cf.field_name} fields={[cf]} values={form.custom_fields} onChange={cfv => setForm(p => ({...p, custom_fields: cfv}))} layout={[item]} />; }
    return null;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserCircle className="w-5 h-5" style={{ color: tc500 }} />
            {editItem ? t("contatos.edit_title") : t("contatos.new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {filteredLayout.map((item, i) => {
              const rendered = renderItem(item);
              if (!rendered) return null;
              return <div key={i} className={item.field_width === "50" ? "col-span-1" : "col-span-2"}>{rendered}</div>;
            })}
          </div>
          {formSections.map((sec, si) => {
            if (!sec.fields?.length) return null;
            return (
              <div key={si}>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{sec.name}</p>
                <div className="border border-slate-200 rounded-lg px-4 py-3 grid grid-cols-2 gap-3">
                  {sec.fields.map((sf, sfi) => { const r = renderItem(sf); if (!r) return null; return <div key={sfi} className={sf.field_width === "50" ? "col-span-1" : "col-span-2"}>{r}</div>; })}
                </div>
              </div>
            );
          })}
          <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90">
            <Check className="w-4 h-4 mr-2" />{t("common.save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>

    {/* Delete tipo confirmation */}
    <AlertDialog open={deleteTipoIdx !== null} onOpenChange={() => setDeleteTipoIdx(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir tipo de contato</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja excluir este tipo de contato? Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { removeTipo(deleteTipoIdx); setDeleteTipoIdx(null); }}
            className="bg-rose-500 hover:bg-rose-600 text-white">
            <Check className="w-4 h-4 mr-2" />{t("common.yes_delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
